export const TRANSLATIONS_DIR_NAMESPACE = 'translations';

// This route is determined by translationOutputDir in configUtils
export const TRANSLATION_ROUTE = `/${TRANSLATIONS_DIR_NAMESPACE}`;

export const DEFAULT_LOCALE_EN_US = 'en-US';

export const APP_NAME = 'Expense Planning Tool';
export const APP_NAME_SHORT = 'xPT';

export enum eCacheKeys {
  UserAuthDetails = '-userAuthDetails'
}

export enum STAGES {
  DEV = 'dev',
  ALPHA = 'alpha',
  BETA = 'beta',
  PROD = 'prod'
}

/**
 * Whether the given stage is a pre-prod environment (alpha or dev) where
 * in-progress features may be exposed. Used to gate features that should not
 * yet be visible in beta/prod.
 */
export const isAlphaOrDevStage = (stage: string): boolean => stage === STAGES.ALPHA || stage === STAGES.DEV;

export const PHONE_TOOL_URL = 'https://phonetool.amazon.com/users/';
export const USER_IMG_BASE_URL = 'https://badgephotos.corp.amazon.com/?uid=';
export const SLACK_CHANNEL_XPT_USERS = 'https://app.slack.com/client/E015GUGD2V6/C07F9AHGBFA';

export const BROADCAST_VIDEO_LINKS = {
  LOCAL_CURRENCY_USD_DEMO: 'https://broadcast.amazon.com/videos/1654284'
};

export const DEVELOPER_LDAP_GROUP = 'expense-planning-tool-dev';

// User types as individual constants
export const FINANCE_ADMIN = 'Finance Admin';
export const FINANCE_OWNER = 'Finance Owner';
export const BUDGET_LEADER = 'Budget Leader';
export const BUDGET_OWNER = 'Budget Owner';

// User types array for easy iteration or selection
export const USER_TYPES = [FINANCE_ADMIN, FINANCE_OWNER, BUDGET_LEADER, BUDGET_OWNER] as const;

// Type for user types
export type UserType = (typeof USER_TYPES)[number];

export enum eSecretManagerKeys {
  XPT_DEV_API_KEY = 'xpt-appsync-apikey',
  XPT_APPSYNC_API = 'xpt-appsync-endpoint',
  AD3_PAPI_API = 'xpt-PAPI',
  EVENTS_API_KEY = 'api_key',
  EVENTS_API_ENDPOINT = 'api-endpoint'
}

export enum eLocalStorageKeys {
  Theme = 'theme',
  UserAuthDetails = 'userAuthDetails-v1',
  UserAccessDetails = 'UserAccessDetails',
  ForecastTemplateDetailsSelection = 'forecastTemplateDetailsSelection',
  XptHomePageWidgetsLayout = 'xptHomePageWidgetsLayout'
}

export enum eEntityStatus {
  Error = 'error',
  Warning = 'warning',
  Success = 'success',
  Stopped = 'stopped',
  Info = 'info',
  Pending = 'pending',
  InProgress = 'in-progress',
  Loading = 'loading',

  // For Scenario Status
  Open = 'Open',
  Closed = 'Closed',
  Locked = 'Locked',

  // generic purpose
  Active = 'Active',
  Inactive = 'Inactive',

  // scenario snapshot status
  Scheduled = 'scheduled'
}

export enum eErrorMessages {
  APP_INITIALIZATION_FAILED = 'An unexpected error occurred. We are working to resolve this.',
  GENERIC_ERROR = 'An unexpected error occurred. Please try again or contact support if the issue persists.',
  API_REQUEST_FAILED = 'Unable to submit the request. Please try again or contact the administrator.',
  API_REQUEST_IN_PROGRESS = 'The request is currently in progress.',
  API_REQUEST_SUCCESS = 'The request was submitted successfully.',
  NO_DATA_FOUND = 'No data found for the selected criteria.',
  GENERIC_ERROR_WITH_TICKET_INFO = 'try reloading once and if issue still persists, please cut us a ticket link provided at bottom of the page',
  NO_DATA_AVAILABLE = 'No Data Available'
}

// 'success' | 'warning' | 'info' | 'error' | 'in-progress'
export enum NotificationType {
  Info = 'info',
  InProgress = 'in-progress',
  Error = 'error',
  Success = 'success'
}

export enum SpecialMetricTitles {
  HomePage = 'xPT-HomePage'
}

export const HEADER_TYPES = {
  ACTUAL: 'Actual',
  FORECAST: 'Forecast'
} as const;

export type HeaderType = (typeof HEADER_TYPES)[keyof typeof HEADER_TYPES];

export enum AuditType {
  All = 'All',
  Capex = 'capex_mapping'
}

export enum OperationType {
  CRUD = 'crud',
  FILE_UPLOAD = 'file_upload'
}

export const SELECT_ALL_OPTION = {
  label: 'Select All',
  value: 'Select All'
};
export const EMPTY_DROPDOWN_ARRAY = [{ label: '-', value: '' }];
export const EMPTY_DROPDOWN_DESC = [{ label: '-', value: '', description: '' }];
export const EMPTY_DROPDOWN_OBJECT = {
  label: '',
  value: ''
};

export const SPLIT_PANEL_DEFAULT_SIZE: number = 420;
export const SPLIT_PANEL_MAX_SIZE: number = 520;

export const RING_BUSINESS_GROUP_SHORT_DESC = 'RNG';

export const S3_DATA_CONSTANTS = {
  MAX_COMBINED_RECORDS: 70000
} as const;
